/**
 * Готовые («стандартные») представления оси для MapTimeline — переиспользуемые
 * между слоями. Каждый пресет = описание домена (для buildTimeScale) и,
 * опционально, набор подписей-групп (topBands).
 *
 * Подключение в per-layer контроллере:
 *   import { DAY_PERIODS_DOMAIN, dayPeriodBands } from './presets';
 *   <MapTimeline domain={DAY_PERIODS_DOMAIN} topBands={dayPeriodBands()} ... />
 */
import { t } from '@lingui/macro';

/**
 * Суточная шкала: 8 трёхчасовых интервалов суток (индексы 0–7).
 * Подпись под рельсой — диапазон часов: «0:00 – 3:00», «3:00 – 6:00» … «21:00 – 24:00».
 * Курсор/значение слоя — индекс интервала (0–7).
 */
export const DAY_PERIODS_DOMAIN = {
    type: 'categorical',
    values: [ 0, 1, 2, 3, 4, 5, 6, 7 ],
    format: idx => `${idx * 3}:00 – ${idx * 3 + 3}:00`,
};

/**
 * Подписи-группы над суточной шкалой (проп topBands): Ночь / Утро / День / Вечер,
 * каждая центрируется между своей парой интервалов (Ночь → 0–3 и 3–6, Утро → 6–9
 * и 9–12, День → 12–15 и 15–18, Вечер → 18–21 и 21–24).
 *
 * Функция, а не константа: макрос t`` резолвит перевод под текущую локаль при
 * каждом вызове (вызывать в рендере контроллера).
 */
export const dayPeriodBands = () => [
    { from: 0, to: 1, label: t`Ночь` },
    { from: 2, to: 3, label: t`Утро` },
    { from: 4, to: 5, label: t`День` },
    { from: 6, to: 7, label: t`Вечер` },
];

/**
 * Части дня классифицированных парковок: 5 НЕравных интервалов (значения 1–5,
 * как в CLASS_DAY_PART_STYLE.parts). Подпись под рельсой — диапазон часов
 * («0–5», «6–11» …), над рельсой (topBands) — название части (Ночь/Утро/…).
 * Курсор/значение слоя — value части (1..5).
 */
const DAY_PART_CLASS_HOURS = {
    1: '0:00 - 5:00',
    2: '6:00 - 11:00',
    3: '12:00 - 15:00',
    4: '16:00 - 19:00',
    5: '20:00 - 23:00',
};

export const DAY_PART_CLASS_DOMAIN = {
    type: 'categorical',
    values: [ 1, 2, 3, 4, 5 ],
    format: v => DAY_PART_CLASS_HOURS[v] ?? '',
};

/**
 * Подписи-группы над шкалой частей дня (проп topBands): каждая центрируется
 * над своим интервалом (from === to === value части).
 * Функция, а не константа: t`` резолвит перевод под текущую локаль в рендере.
 */
export const dayPartClassBands = () => [
    { from: 1, to: 1, label: t`Ночь` },
    { from: 2, to: 2, label: t`Утро` },
    { from: 3, to: 3, label: t`День` },
    { from: 4, to: 4, label: t`Вечер` },
    { from: 5, to: 5, label: t`Поздний вечер` },
];

// ─── Годовая шкала с разбивкой по месяцам (используется в режиме «Сутки» слоя
//     «Фактический ребаланс» и в «Перебросках флота») ──────────────────────────
const MS_DAY = 86400000;
// ISO 'YYYY-MM-DD' → номер дня (UTC) и обратно — целочисленный домен дней рельсы.
export const dayNum = iso => {
    if (!iso) return null;
    const [ y, m, d ] = iso.split('-').map(Number);
    return Math.floor(Date.UTC(y, m - 1, d) / MS_DAY);
};
export const isoFromDay = n => new Date(n * MS_DAY).toISOString().slice(0, 10);
const fmtDDMM = iso => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

/**
 * Стандартная годовая шкала оси: весь календарный год (Январь–Декабрь) в
 * continuous-домене по номеру дня. Начала всех 12 месяцев — точки (rc-slider dot);
 * подпись (полное название месяца) только на нечётных (Январь, Март, … Ноябрь), на
 * чётных точка без подписи. Недельные штрихи (minorTicks) делят каждый месяц на 4.
 *
 * @param {number} year  — календарный год
 * @param {string} [locale] — локаль для названий месяцев (по умолчанию 'ru')
 * @returns {{ domain, weekTicks, yearMinDay, yearMaxDay }}
 *   domain     — прокидывать в <MapTimeline domain={...} />
 *   weekTicks  — прокидывать в minorTicks={weekTicks}
 *   yearMin/MaxDay — границы года в номерах дня (для клампа курсора/диапазона)
 */
export const yearMonthScale = (year, locale = 'ru') => {
    const yearMinDay = dayNum(`${year}-01-01`);
    const yearMaxDay = dayNum(`${year}-12-31`);
    const monthStartDay = mIdx => dayNum(`${year}-${String(mIdx + 1).padStart(2, '0')}-01`);
    const monthName = mIdx => {
        const s = new Intl.DateTimeFormat(locale || 'ru', { month: 'long' }).format(new Date(year, mIdx, 1));
        return s.charAt(0).toUpperCase() + s.slice(1);
    };
    const inRail = d => d >= yearMinDay && d <= yearMaxDay;
    const monthTicks = Array.from({ length: 12 }, (_, mIdx) => ({
        value: monthStartDay(mIdx),
        label: mIdx % 2 === 0 ? monthName(mIdx) : '',
    })).filter(tk => inRail(tk.value));
    const weekTicks = [];
    for (let mIdx = 0; mIdx < 12; mIdx += 1) {
        const start = monthStartDay(mIdx);
        const next = mIdx < 11 ? monthStartDay(mIdx + 1) : dayNum(`${year + 1}-01-01`);
        const len = next - start;
        for (let k = 1; k < 4; k += 1) {
            const d = Math.round(start + (len * k) / 4);
            if (inRail(d)) weekTicks.push(d);
        }
    }
    const domain = {
        type: 'continuous',
        values: [ yearMinDay, yearMaxDay ],
        step: 1,
        format: n => fmtDDMM(isoFromDay(Math.round(n))),
        ticks: monthTicks,
    };
    return { domain, weekTicks, yearMinDay, yearMaxDay };
};
